/* global google */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Text } from '../../atoms/text';
import { useI18n } from '../../../hooks/provider-bounded/i18n';
import {
  getConfiguredGoogleMapsApiKey,
  getConfiguredGoogleMapsMapId,
  loadGoogleMapsApi,
  loadGoogleMapsMarkerLibrary,
} from '../../../utils/location/google-maps.loader';

interface LocationMapPickerProps {
  latitude?: number | null;
  longitude?: number | null;
  countryCode?: string | null;
  disabled?: boolean;
  /** Display the location without map interactions. */
  readOnly?: boolean;
  /** Override preview dimensions within the owning layout. */
  className?: string;
  onCoordinatesChange: (coordinates: {
    latitude: number;
    longitude: number;
  }) => void;
}

const DEFAULT_COUNTRY_CENTER: Record<string, google.maps.LatLngLiteral> = {
  CL: { lat: -33.4489, lng: -70.6693 },
  CO: { lat: 4.711, lng: -74.0721 },
};

const DEFAULT_CENTER: google.maps.LatLngLiteral = {
  lat: -33.4489,
  lng: -70.6693,
};
const DEFAULT_MAP_ID = 'DEMO_MAP_ID';
const DEFAULT_ZOOM = 16;
const EMPTY_ZOOM = 6;

type GoogleAdvancedMarker = google.maps.marker.AdvancedMarkerElement;

const safelyDetachMarker = (
  markerRef: React.MutableRefObject<GoogleAdvancedMarker | null>,
  markerDragListenerRef: React.MutableRefObject<google.maps.MapsEventListener | null>
): void => {
  markerDragListenerRef.current?.remove();
  markerDragListenerRef.current = null;

  const marker = markerRef.current;
  if (!marker) {
    return;
  }

  try {
    if (marker.map != null) {
      marker.map = null;
    }
  } catch {
    // AdvancedMarkerElement teardown can throw when Google already detached the
    // internal DOM node. Swallow the error so React unmounts safely.
  }

  markerRef.current = null;
};

const getMarkerCoordinates = (
  marker: GoogleAdvancedMarker
): google.maps.LatLngLiteral | null => {
  const position = marker.position;
  if (!position) {
    return null;
  }

  if (
    typeof position === 'object' &&
    'lat' in position &&
    typeof position.lat === 'number' &&
    'lng' in position &&
    typeof position.lng === 'number'
  ) {
    return {
      lat: position.lat,
      lng: position.lng,
    };
  }

  if (
    typeof position === 'object' &&
    'lat' in position &&
    typeof position.lat === 'function' &&
    'lng' in position &&
    typeof position.lng === 'function'
  ) {
    return {
      lat: position.lat(),
      lng: position.lng(),
    };
  }

  return null;
};

const resolveCenter = (params: {
  latitude?: number | null;
  longitude?: number | null;
  countryCode?: string | null;
}): google.maps.LatLngLiteral => {
  if (params.latitude != null && params.longitude != null) {
    return {
      lat: params.latitude,
      lng: params.longitude,
    };
  }

  const countryCode = params.countryCode?.trim().toUpperCase();
  return (
    (countryCode ? DEFAULT_COUNTRY_CENTER[countryCode] : undefined) ??
    DEFAULT_CENTER
  );
};

/**
 * Shared interactive Google Maps picker used by the property editor location
 * section.
 */
export const LocationMapPicker: React.FC<LocationMapPickerProps> = ({
  latitude,
  longitude,
  countryCode,
  disabled = false,
  readOnly = false,
  className = '',
  onCoordinatesChange,
}) => {
  const { t } = useI18n();
  const mapElementRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<GoogleAdvancedMarker | null>(null);
  const mapClickListenerRef = useRef<google.maps.MapsEventListener | null>(
    null
  );
  const markerDragListenerRef = useRef<google.maps.MapsEventListener | null>(
    null
  );
  const [loadingError, setLoadingError] = useState<string | null>(null);

  const hasCoordinates = latitude != null && longitude != null;
  const center = useMemo(
    () =>
      resolveCenter({
        latitude,
        longitude,
        countryCode,
      }),
    [countryCode, latitude, longitude]
  );

  useEffect(() => {
    let active = true;

    const initializeMap = async () => {
      if (!mapElementRef.current) {
        return;
      }

      try {
        const [maps, markerLibrary] = await Promise.all([
          loadGoogleMapsApi(),
          loadGoogleMapsMarkerLibrary(),
        ]);
        if (!active) {
          return;
        }

        const map =
          mapRef.current ??
          new maps.Map(mapElementRef.current, {
            center,
            zoom: hasCoordinates ? DEFAULT_ZOOM : EMPTY_ZOOM,
            disableDefaultUI: true,
            zoomControl: !readOnly,
            gestureHandling: readOnly ? 'none' : 'auto',
            keyboardShortcuts: !readOnly,
            fullscreenControl: false,
            streetViewControl: false,
            mapTypeControl: false,
            clickableIcons: false,
            mapId: getConfiguredGoogleMapsMapId() ?? DEFAULT_MAP_ID,
          });

        mapRef.current = map;
        setLoadingError(null);

        map.setCenter(center);
        map.setZoom(hasCoordinates ? DEFAULT_ZOOM : EMPTY_ZOOM);

        mapClickListenerRef.current?.remove();
        mapClickListenerRef.current = map.addListener(
          'click',
          (event: google.maps.MapMouseEvent) => {
            if (disabled || readOnly || !event.latLng) {
              return;
            }

            onCoordinatesChange({
              latitude: event.latLng.lat(),
              longitude: event.latLng.lng(),
            });
          }
        );

        const shouldShowMarker = latitude != null && longitude != null;
        if (!shouldShowMarker) {
          safelyDetachMarker(markerRef, markerDragListenerRef);
          return;
        }

        const marker =
          markerRef.current ??
          new markerLibrary.AdvancedMarkerElement({
            map,
            gmpDraggable: !disabled && !readOnly,
            title: t('shared.location.map.markerTitle'),
          });

        markerRef.current = marker;
        marker.map = map;
        marker.gmpDraggable = !disabled && !readOnly;
        marker.position = {
          lat: latitude,
          lng: longitude,
        };

        markerDragListenerRef.current?.remove();
        markerDragListenerRef.current = marker.addListener('dragend', () => {
          if (disabled || readOnly) return;
          const position = getMarkerCoordinates(marker);
          if (!position) {
            return;
          }

          onCoordinatesChange({
            latitude: position.lat,
            longitude: position.lng,
          });
        });
      } catch (error) {
        if (!active) {
          return;
        }

        setLoadingError(
          error instanceof Error
            ? error.message
            : t('shared.location.map.loadError')
        );
      }
    };

    void initializeMap();

    return () => {
      active = false;
    };
  }, [
    readOnly,
    center,
    disabled,
    hasCoordinates,
    latitude,
    longitude,
    onCoordinatesChange,
    t,
  ]);

  useEffect(() => {
    return () => {
      mapClickListenerRef.current?.remove();
      mapClickListenerRef.current = null;
      safelyDetachMarker(markerRef, markerDragListenerRef);
    };
  }, []);

  if (!getConfiguredGoogleMapsApiKey()) {
    return (
      <div
        className={`flex h-80 items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center ${className}`}
      >
        <Text size="small" className="text-slate-500">
          {t('shared.location.map.configurationMissing')}
        </Text>
      </div>
    );
  }

  return (
    <div
      className={`relative h-80 overflow-hidden rounded-2xl border border-slate-200 bg-white ${className}`}
    >
      <div ref={mapElementRef} className="h-full w-full" />
      {loadingError ? (
        <div className="absolute inset-x-4 bottom-4 rounded-xl bg-white/95 px-4 py-3 shadow-lg">
          <Text size="small" className="text-slate-500">
            {loadingError}
          </Text>
        </div>
      ) : null}
    </div>
  );
};
